import * as React from 'react';
import { AiSurface } from '@/components/ui/ai-surface';
import { VendorCitation } from '@/components/ui/citation-chip';
import { ErrorMessage } from '@/components/ui/error-message';
import { RATING_LABEL, RatingChip } from '@/components/ui/rating-chip';
import { StatusChip } from '@/components/ui/status-chip';
import {
  PanelSection,
  SegmentedControl,
  SidePanel,
  WorkspaceButton,
  WorkspaceField,
  workspaceFieldClass,
} from '@/components/ui/workspace';
import {
  RATINGS,
  type EvaluatedModule,
  type JudgeRun,
  type ProductInScope,
  type Rating,
  type VendorRating,
} from '@/lib/api/vendor-intelligence';
import { cn } from '@/lib/utils/cn';
import {
  ANSWER_LEGEND,
  SOURCE_LABEL,
  formatDate,
  judgeReasonText,
  toVendorCitation,
} from './vi-logic';

/**
 * The rating side panel (T-0267; E14-design §3): one product in one
 * module. The rating, where it came from and why, its citations with their
 * excerpts (and the answer counts for a rating from the vendor's answers),
 * then Confirm and Change. A change needs a reason of at least ten
 * characters; it is confirmed with source "consultant", and the earlier
 * rating stays in the audit history.
 */

export const MIN_REASON_CHARS = 10;

export function RatingPanel({
  product,
  module,
  rating,
  run,
  canEdit,
  busy,
  error,
  onClose,
  onConfirm,
  onChange,
}: {
  product: ProductInScope;
  module: EvaluatedModule;
  rating: VendorRating | null;
  run: JudgeRun | null;
  canEdit: boolean;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: (rating: VendorRating) => void;
  onChange: (rating: VendorRating, next: Rating, reason: string) => Promise<unknown>;
}) {
  const [changing, setChanging] = React.useState(false);
  const [next, setNext] = React.useState<Rating>(rating?.rating ?? 'unknown');
  const [reason, setReason] = React.useState('');
  React.useEffect(() => {
    setChanging(false);
    setNext(rating?.rating ?? 'unknown');
    setReason('');
  }, [rating?.id, rating?.rating]);

  const trimmed = reason.replace(/\s+/g, ' ').trim();
  const reasonShort = trimmed.length < MIN_REASON_CHARS;

  const footer =
    rating && canEdit ? (
      changing ? (
        <>
          <WorkspaceButton variant="tertiary" onClick={() => setChanging(false)} disabled={busy}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            variant="primary"
            disabled={busy || reasonShort}
            onClick={() => {
              void onChange(rating, next, trimmed).then(() => setChanging(false));
            }}
          >
            Save and confirm
          </WorkspaceButton>
        </>
      ) : (
        <>
          <WorkspaceButton onClick={() => setChanging(true)} disabled={busy}>
            Change
          </WorkspaceButton>
          {rating.status === 'proposed' ? (
            <WorkspaceButton variant="primary" onClick={() => onConfirm(rating)} disabled={busy}>
              Confirm
            </WorkspaceButton>
          ) : null}
        </>
      )
    ) : undefined;

  return (
    <SidePanel
      aria-label={`Rating of ${product.name} for ${module.name}`}
      identifier={product.name}
      path={`${product.vendor.name} · ${module.name}`}
      onClose={onClose}
      footer={footer}
    >
      {rating ? (
        <PanelSection label="Rating">
          <div className="flex flex-wrap items-center gap-2">
            <RatingChip
              rating={rating.rating}
              state={rating.status === 'confirmed' ? 'confirmed' : 'proposed'}
            />
            <span className="text-body-sm text-ink-700">
              {rating.status === 'confirmed'
                ? `Confirmed${rating.confirmed_by ? ` by ${rating.confirmed_by.name}` : ''}${
                    rating.confirmed_at ? ` on ${formatDate(rating.confirmed_at)}` : ''
                  }`
                : rating.source === 'vendor_answers'
                  ? "Proposed from the vendor's answers. It counts once you confirm it."
                  : 'AI-proposed. It counts once you confirm it.'}
            </span>
          </div>
          <p className="mb-0 mt-1.5 text-meta text-ink-500">{SOURCE_LABEL[rating.source]}</p>
        </PanelSection>
      ) : (
        <PanelSection label="Rating">
          <p className="m-0 text-body-sm text-ink-500">
            {run && run.status !== 'succeeded' && run.status !== 'running'
              ? judgeReasonText(run.reason)
              : run?.status === 'running'
                ? 'The AI is reading the evidence now.'
                : 'Not rated yet. Propose ratings to have one proposed.'}
          </p>
        </PanelSection>
      )}

      {changing && rating ? (
        <PanelSection label="Change the rating">
          <SegmentedControl
            aria-label="New rating"
            value={next}
            onChange={setNext}
            options={RATINGS.map((r) => ({ value: r, label: RATING_LABEL[r] }))}
          />
          <WorkspaceField
            className="mt-3"
            label="Reason"
            help={`At least ${MIN_REASON_CHARS} characters. It goes in the audit history.`}
          >
            {(id, describedBy) => (
              <textarea
                id={id}
                aria-describedby={describedBy}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className={cn(workspaceFieldClass, 'min-h-[76px] resize-y py-2 leading-[20px]')}
              />
            )}
          </WorkspaceField>
        </PanelSection>
      ) : null}

      {rating?.change_reason ? (
        <PanelSection label="Why it was changed">
          <p className="m-0 text-body-sm text-ink-900">{rating.change_reason}</p>
        </PanelSection>
      ) : null}

      {rating?.rationale ? (
        <PanelSection
          label={
            rating.source === 'vendor_answers'
              ? "Rated from the vendor's answers"
              : rating.source === 'consultant'
                ? 'What was proposed first'
                : 'Rationale'
          }
        >
          {rating.source === 'knowledge_items' ? (
            <AiSurface className="rounded-card px-3 py-2 text-body-sm">
              {rating.rationale}
            </AiSurface>
          ) : (
            <p className="m-0 text-body-sm text-ink-900">{rating.rationale}</p>
          )}
        </PanelSection>
      ) : null}

      {rating?.answers_summary ? (
        <PanelSection label="The vendor's answers">
          <div className="flex flex-wrap gap-1.5" aria-label="Answers by code">
            {(['S', 'C', 'T', 'F', 'N'] as const).map((code) => (
              <StatusChip key={code} tone="neutral" size="sm">
                {code} {rating.answers_summary!.counts[code]}
              </StatusChip>
            ))}
          </div>
          <p className="mb-0 mt-1.5 text-meta text-ink-500">{ANSWER_LEGEND}</p>
          <p className="mb-0 mt-1 text-meta text-ink-500">
            {rating.answers_summary.answered} of {rating.answers_summary.requirements} requirements
            answered, weight {rating.answers_summary.answered_weight} of{' '}
            {rating.answers_summary.module_weight}.
          </p>
        </PanelSection>
      ) : null}

      {rating ? (
        <PanelSection label={`Evidence (${rating.citations.length})`}>
          {rating.citations.length === 0 ? (
            <p className="m-0 text-body-sm text-ink-500">
              No item is cited: there is no evidence for this module yet.
            </p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {rating.citations.map((c) => (
                <li key={c.item_id}>
                  <div className="flex flex-wrap items-center gap-1.5 text-meta text-ink-500">
                    {c.number ? (
                      <span className="font-mono font-medium text-ink-700">{c.number}</span>
                    ) : null}
                    <span>{c.item_kind.replace(/_/g, ' ')}</span>
                    {c.answer_code ? (
                      <StatusChip tone="neutral" size="sm">
                        {c.answer_code}
                        {c.answer_label ? ` · ${c.answer_label}` : ''}
                      </StatusChip>
                    ) : null}
                  </div>
                  {c.statement ? (
                    <p className="mb-0 mt-1 text-body-sm text-ink-900">{c.statement}</p>
                  ) : null}
                  <VendorCitation citation={toVendorCitation(c)} />
                </li>
              ))}
            </ul>
          )}
        </PanelSection>
      ) : null}

      {error ? <ErrorMessage error={error} /> : null}
    </SidePanel>
  );
}
